// packages/components/calendar/Calendar.js
import React, { useEffect, useState } from "react";

import CalendarHeader from "./header";
import CalendarMonth from "./month";
import { CalendarShimmer } from "./loader";
import CalendarWeekdays from "./week";
import EventsList from "./event-list";
import Loader from "../loader";
import { VIEW_TYPES } from "./types";
import useCalendar from "./useCalendar";

/**
 * A headless calendar component that handles calendar functionality
 * while allowing customization of rendering and behavior
 *
 * @param {import('./types').CalendarProps} props
 */
const Calendar = ({
  date,
  setDate,
  renderDay,
  renderHeader,
  renderWeekdays,
  isLoading,
  events = {},
  viewOptions = { showAdjacentMonths: true },
  locale = "en-US",
  className = "",
  onAddEvent = () => {},
  isHeaderPortal = false,
}) => {
  // State for the current view (day, week, month, year)
  const [currentView, setCurrentView] = useState(VIEW_TYPES.MONTH);

  // State for tracking whether we're in desktop view
  const [isDesktop, setIsDesktop] = useState(false);

  // Use the calendar hook to get all the calendar data and handlers
  const {
    days,
    selectedDay,
    handleSelectDate,
    handlePrevMonth,
    handleNextMonth,
    handleToday,
  } = useCalendar({
    date,
    setDate,
    events,
    showAdjacentMonths: viewOptions.showAdjacentMonths,
    locale,
  });

  // Effect to detect desktop view
  useEffect(() => {
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024); // lg breakpoint in Tailwind
    };

    checkIsDesktop();
    window.addEventListener("resize", checkIsDesktop);

    return () => {
      window.removeEventListener("resize", checkIsDesktop);
    };
  }, []);

  // Handle view change (day, week, month, year)
  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  return (
    <div className={`lg:flex lg:h-full lg:flex-col ${className}`}>
      <CalendarHeader
        currentDate={date}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onToday={handleToday}
        currentView={currentView}
        onViewChange={handleViewChange}
        onAddEvent={onAddEvent}
        renderHeader={renderHeader}
        locale={locale}
        isHeaderPortal={isHeaderPortal}
      />

      {isLoading ? (
        <div className="shadow-sm ring-1 ring-black/5 lg:flex   lg:flex-col mx-6">
          <CalendarWeekdays locale={locale} renderWeekdays={renderWeekdays} />
          <CalendarShimmer />
        </div>
      ) : (
        <div className="shadow-sm ring-1 ring-black/5 lg:flex  lg:flex-col mx-6">
          <CalendarWeekdays locale={locale} renderWeekdays={renderWeekdays} />
          {isLoading ? (
            <Loader className="w-full h-full flex-auto" />
          ) : (
            <div className="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto">
              <CalendarMonth
                days={days}
                onSelectDate={handleSelectDate}
                renderDay={renderDay}
                isDesktop={isDesktop}
              />
            </div>
          )}
        </div>
      )}

      {/* <EventsList selectedDay={selectedDay} /> */}
    </div>
  );
};

export default Calendar;
